import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../../../services/api';

function FeeHeader({ title }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg">
        <i className="ri-bank-card-line text-slate-500 text-base" aria-hidden />
      </div>
      <h3 className="text-sm font-semibold text-slate-800 flex-1">{title}</h3>
    </div>
  );
}

function numberLocaleFromI18n(lang) {
  const l = (lang || 'en').toLowerCase();
  if (l.startsWith('es')) return 'es-ES';
  if (l.startsWith('en')) return 'en-GB';
  return lang || undefined;
}

/**
 * Advisor service fee: read / edit / confirm (Reddy-aligned). Persists via PATCH case-extensions.
 */
const DEFAULT_DISPLAY_CAMPAIGN = 'Cruz Roja Partnership';
const DEFAULT_DISPLAY_CAMPAIGN_CODE = 'K72841';

export default function ServiceFeeCard({
  userId,
  sessionId,
  serviceFeeEur,
  feeCampaignCode,
  feeCampaignLabel,
  feeCampaignDefaultEur,
  applicantReferralToken,
  /** Shown when there is a share token but no campaign label from the API (future: portal-driven). */
  feeCampaignFallbackLabel = DEFAULT_DISPLAY_CAMPAIGN,
  onAfterSave,
  onToast,
}) {
  const { t, i18n } = useTranslation();
  const numberLocale = useMemo(() => numberLocaleFromI18n(i18n.language), [i18n.language]);

  const DEFAULT_FEE = 200;
  const feeValue =
    serviceFeeEur != null && !Number.isNaN(Number(serviceFeeEur)) ? Number(serviceFeeEur) : DEFAULT_FEE;
  const applicantToken = (applicantReferralToken || '').trim().toUpperCase() || '';
  const feeCampaignCodeNorm = (feeCampaignCode || '').trim().toUpperCase();
  const feeCampaignLabelNorm = (feeCampaignLabel || '').trim();
  const feeCampaignDefault =
    feeCampaignDefaultEur != null && !Number.isNaN(Number(feeCampaignDefaultEur))
      ? Number(feeCampaignDefaultEur)
      : 0;

  const fallbackCampaignDisplay = (feeCampaignFallbackLabel || DEFAULT_DISPLAY_CAMPAIGN).trim();
  const displayCampaignCode = feeCampaignCodeNorm || DEFAULT_DISPLAY_CAMPAIGN_CODE;
  const displayCampaignLabel = feeCampaignLabelNorm || fallbackCampaignDisplay;
  const showCampaignFeeEuro =
    Boolean(feeCampaignCodeNorm) ||
    Boolean(feeCampaignLabelNorm) ||
    (Boolean(applicantToken) && Boolean(displayCampaignLabel));

  const [feeEditing, setFeeEditing] = useState(false);
  const [feeConfirming, setFeeConfirming] = useState(false);
  const [feeDraftAmount, setFeeDraftAmount] = useState(feeValue);
  const [feeDraftCampaignCode, setFeeDraftCampaignCode] = useState(feeCampaignCodeNorm);
  const [feeDraftCampaignLabel, setFeeDraftCampaignLabel] = useState(feeCampaignLabelNorm);
  const [feeDraftCampaignDefault, setFeeDraftCampaignDefault] = useState(feeCampaignDefault);
  const [feeDraftLookupDone, setFeeDraftLookupDone] = useState(Boolean(feeCampaignCodeNorm && feeCampaignLabelNorm));
  const [feeDraftLookupError, setFeeDraftLookupError] = useState(false);
  const [feeDraftReason, setFeeDraftReason] = useState('');
  const [feeSaving, setFeeSaving] = useState(false);

  useEffect(() => {
    if (feeEditing || feeConfirming) return;
    setFeeDraftAmount(feeValue);
    setFeeDraftCampaignCode(feeCampaignCodeNorm);
    setFeeDraftCampaignLabel(feeCampaignLabelNorm);
    setFeeDraftCampaignDefault(feeCampaignDefault);
    setFeeDraftLookupDone(Boolean(feeCampaignCodeNorm && feeCampaignLabelNorm));
    setFeeDraftLookupError(false);
    setFeeDraftReason('');
  }, [
    feeValue,
    feeCampaignCodeNorm,
    feeCampaignLabelNorm,
    feeCampaignDefault,
    feeEditing,
    feeConfirming,
  ]);

  const handleCampaignLookup = useCallback(async () => {
    const code = feeDraftCampaignCode.trim().toUpperCase();
    if (!code) return;
    try {
      const res = await mvpApi.lookupFeeCampaign(code);
      const d = res?.data;
      if (d?.found && d.code) {
        setFeeDraftCampaignLabel(d.label || '');
        const def = Number(d.default_fee_eur);
        setFeeDraftCampaignDefault(def);
        setFeeDraftAmount(def);
        setFeeDraftLookupDone(true);
        setFeeDraftLookupError(false);
      } else {
        setFeeDraftCampaignLabel('');
        setFeeDraftCampaignDefault(0);
        setFeeDraftLookupDone(false);
        setFeeDraftLookupError(true);
      }
    } catch {
      setFeeDraftCampaignLabel('');
      setFeeDraftCampaignDefault(0);
      setFeeDraftLookupDone(false);
      setFeeDraftLookupError(true);
    }
  }, [feeDraftCampaignCode]);

  const hasCampaign = feeDraftLookupDone && !!feeDraftCampaignLabel;
  const isOverrideEdit = hasCampaign && Math.abs(feeDraftAmount - feeDraftCampaignDefault) > 0.005;
  const canContinue = feeDraftAmount >= 0 && (!isOverrideEdit || feeDraftReason.trim() !== '');
  const isOverrideReadOnly = feeCampaignDefault > 0 && Math.abs(feeValue - feeCampaignDefault) > 0.005;
  const isOverrideConfirm = hasCampaign && Math.abs(feeDraftAmount - feeDraftCampaignDefault) > 0.005;

  const fmtEur = useCallback(
    (n) => `€${Number(n).toLocaleString(numberLocale)}`,
    [numberLocale],
  );

  const startEdit = () => {
    setFeeDraftCampaignCode(feeCampaignCodeNorm);
    setFeeDraftCampaignLabel(feeCampaignLabelNorm);
    setFeeDraftCampaignDefault(feeCampaignDefault);
    setFeeDraftAmount(feeValue);
    setFeeDraftLookupDone(Boolean(feeCampaignCodeNorm && feeCampaignLabelNorm));
    setFeeDraftLookupError(false);
    setFeeDraftReason('');
    setFeeEditing(true);
  };

  const cancelEdit = () => {
    setFeeEditing(false);
    setFeeConfirming(false);
    setFeeDraftCampaignCode(feeCampaignCodeNorm);
    setFeeDraftCampaignLabel(feeCampaignLabelNorm);
    setFeeDraftCampaignDefault(feeCampaignDefault);
    setFeeDraftAmount(feeValue);
    setFeeDraftLookupDone(Boolean(feeCampaignCodeNorm && feeCampaignLabelNorm));
    setFeeDraftLookupError(false);
    setFeeDraftReason('');
  };

  const confirmSave = async () => {
    setFeeSaving(true);
    try {
      const payload = {
        apply_service_fee: true,
        service_fee_eur: feeDraftAmount,
        fee_override_reason: feeDraftReason.trim() || undefined,
      };
      if (feeDraftLookupDone && feeDraftCampaignCode.trim()) {
        payload.fee_campaign_code = feeDraftCampaignCode.trim().toUpperCase();
        payload.fee_campaign_label = feeDraftCampaignLabel;
        payload.fee_campaign_default_eur = feeDraftCampaignDefault;
      }
      await mvpApi.patchCaseExtensions(userId, sessionId, payload);
      onToast?.(t('advisorPortal.serviceFeeToastUpdated'));
      setFeeConfirming(false);
      setFeeEditing(false);
      await onAfterSave?.();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      onToast?.(typeof raw === 'string' ? raw : t('advisorPortal.serviceFeeToastSaveFailed'));
    } finally {
      setFeeSaving(false);
    }
  };

  const feeTitle = t('advisorPortal.serviceFeeTitle');
  const editAria = t('advisorPortal.serviceFeeEditAria');

  if (feeConfirming) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <FeeHeader title={feeTitle} />
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 space-y-2.5">
          <p className="text-xs font-semibold text-slate-700">{t('advisorPortal.serviceFeeConfirmChanges')}</p>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-400 uppercase tracking-wide font-medium">
              {t('advisorPortal.serviceFeeLabel')}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-slate-900">{fmtEur(feeDraftAmount)}</span>
              {isOverrideConfirm ? (
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                  {t('advisorPortal.serviceFeeOverrideBadge')}
                </span>
              ) : null}
            </div>
          </div>
          {feeDraftLookupDone && feeDraftCampaignLabel ? (
            <div className="flex items-start justify-between gap-2">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide font-medium shrink-0">
                {t('advisorPortal.serviceFeeCampaign')}
              </span>
              <span className="text-xs font-medium text-slate-700 text-right">
                <span className="font-mono">{feeDraftCampaignCode}</span>
                <span className="text-slate-500 font-normal ml-1">{feeDraftCampaignLabel}</span>
              </span>
            </div>
          ) : feeCampaignCodeNorm && !feeDraftCampaignCode.trim() ? (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide font-medium">
                {t('advisorPortal.serviceFeeCampaign')}
              </span>
              <span className="text-xs text-rose-500 font-medium">{t('advisorPortal.serviceFeeRemoved')}</span>
            </div>
          ) : null}
          {feeDraftReason ? (
            <div className="flex items-start justify-between gap-2">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide font-medium shrink-0">
                {t('advisorPortal.serviceFeeReason')}
              </span>
              <span className="text-xs text-slate-600 text-right">{feeDraftReason}</span>
            </div>
          ) : null}
          <p className="text-[10px] text-slate-400 pt-0.5">{t('advisorPortal.serviceFeeHistoryRecorded')}</p>
        </div>
        <div className="flex flex-col gap-2 min-[420px]:flex-row">
          <button
            type="button"
            disabled={feeSaving}
            onClick={() => confirmSave()}
            className="min-h-[44px] flex-1 text-xs font-semibold py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {feeSaving ? t('advisorPortal.serviceFeeSaving') : t('advisorPortal.serviceFeeConfirm')}
          </button>
          <button
            type="button"
            disabled={feeSaving}
            onClick={() => setFeeConfirming(false)}
            className="min-h-[44px] flex-1 text-xs font-medium py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('advisorPortal.serviceFeeBack')}
          </button>
        </div>
      </div>
    );
  }

  if (feeEditing) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <FeeHeader title={feeTitle} />
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] uppercase tracking-wide font-medium text-slate-400">
                {t('advisorPortal.serviceFeeAmountLabel', { currency: '€' })}
              </label>
              {isOverrideEdit ? (
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                  {t('advisorPortal.serviceFeeOverrideBadge')}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 min-[420px]:flex-row min-[420px]:items-center">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium pointer-events-none">
                  €
                </span>
                <input
                  type="number"
                  min={0}
                  value={Number.isNaN(feeDraftAmount) ? '' : feeDraftAmount}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : Number(e.target.value);
                    setFeeDraftAmount(Number.isNaN(val) ? 0 : val);
                    const hc = feeDraftLookupDone && !!feeDraftCampaignLabel;
                    if (!hc || Math.abs(val - feeDraftCampaignDefault) <= 0.005) setFeeDraftReason('');
                  }}
                  className={`w-full min-h-[44px] text-sm border rounded-lg pl-7 pr-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-400 bg-white transition ${
                    isOverrideEdit ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'
                  }`}
                />
              </div>
              {isOverrideEdit ? (
                <button
                  type="button"
                  onClick={() => {
                    setFeeDraftAmount(feeDraftCampaignDefault);
                    setFeeDraftReason('');
                  }}
                  className="min-h-[40px] text-[11px] text-slate-500 hover:text-slate-700 whitespace-nowrap cursor-pointer transition-colors"
                >
                  {t('advisorPortal.serviceFeeReset')}
                </button>
              ) : null}
            </div>
          </div>
          {isOverrideEdit ? (
            <div>
              <label className="text-[11px] uppercase tracking-wide font-medium text-slate-400 mb-1 block">
                {t('advisorPortal.serviceFeeReasonOverride')} <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                value={feeDraftReason}
                onChange={(e) => setFeeDraftReason(e.target.value)}
                placeholder={t('advisorPortal.serviceFeeReasonPlaceholderEdit')}
                className="w-full min-h-[44px] text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-400 bg-white placeholder-slate-300 transition"
              />
            </div>
          ) : null}
          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-wide font-medium text-slate-400">
                {t('advisorPortal.serviceFeeCampaignCodeLabel')}{' '}
                <span className="normal-case text-slate-300 font-normal">
                  {t('advisorPortal.serviceFeeCampaignCodeOptional')}
                </span>
              </label>
              {hasCampaign ? (
                <button
                  type="button"
                  onClick={() => {
                    setFeeDraftCampaignCode('');
                    setFeeDraftCampaignLabel('');
                    setFeeDraftCampaignDefault(0);
                    setFeeDraftLookupDone(false);
                    setFeeDraftLookupError(false);
                    setFeeDraftReason('');
                  }}
                  className="text-[10px] text-rose-400 hover:text-rose-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  {t('advisorPortal.serviceFeeRemove')}
                </button>
              ) : null}
            </div>
            {hasCampaign ? (
              <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <i className="ri-check-line text-slate-500 text-xs shrink-0" aria-hidden />
                <span className="text-xs font-semibold text-slate-700 font-mono">{feeDraftCampaignCode}</span>
                <span className="text-xs text-slate-500 flex-1 truncate">{feeDraftCampaignLabel}</span>
                <span className="text-[10px] text-slate-400 shrink-0">{fmtEur(feeDraftCampaignDefault)}</span>
              </div>
            ) : (
              <div>
                <div className="flex flex-col gap-2 min-[420px]:flex-row">
                  <input
                    type="text"
                    value={feeDraftCampaignCode}
                    onChange={(e) => {
                      setFeeDraftCampaignCode(e.target.value.toUpperCase());
                      setFeeDraftLookupDone(false);
                      setFeeDraftLookupError(false);
                      setFeeDraftCampaignLabel('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCampaignLookup();
                    }}
                    placeholder={t('advisorPortal.serviceFeeCampaignPlaceholder')}
                    maxLength={10}
                    className="min-h-[44px] min-w-0 flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-400 bg-white placeholder-slate-300 font-mono transition"
                  />
                  <button
                    type="button"
                    onClick={() => handleCampaignLookup()}
                    disabled={feeDraftCampaignCode.trim() === ''}
                    className="min-h-[44px] text-xs font-medium px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('advisorPortal.serviceFeeLookup')}
                  </button>
                </div>
                {feeDraftLookupError ? (
                  <p className="mt-1.5 text-xs text-rose-500 flex items-center gap-1">
                    <i className="ri-close-circle-line text-xs" aria-hidden />
                    {t('advisorPortal.serviceFeeCodeNotFound')}
                  </p>
                ) : null}
                <p className="text-[10px] text-slate-400 mt-1">{t('advisorPortal.serviceFeeCampaignHintEdit')}</p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 pt-0.5 min-[420px]:flex-row">
            <button
              type="button"
              onClick={() => {
                if (canContinue) setFeeConfirming(true);
              }}
              disabled={!canContinue}
              className={`min-h-[44px] flex-1 text-xs font-semibold py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                canContinue ? 'bg-slate-900 text-white hover:bg-slate-800' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {t('advisorPortal.serviceFeeContinue')}
            </button>
            <button
              type="button"
              onClick={() => cancelEdit()}
              className="min-h-[44px] flex-1 text-xs font-medium py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('advisorPortal.serviceFeeCancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{t('advisorPortal.defaultCaseType')}</p>
        <button
          type="button"
          onClick={() => startEdit()}
          className="text-slate-400 hover:text-slate-600 cursor-pointer"
          aria-label={editAria}
        >
          <i className="ri-pencil-line text-sm" />
        </button>
      </div>
      <div className="flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-medium text-slate-800">{t('advisorPortal.defaultCaseType')}</p>
          <p className="text-[12px] text-slate-500 mt-0.5">plus government filing fee (€38.28)</p>
          <div className="mt-1.5 flex items-start justify-between gap-3">
            <span className="text-[10px] text-slate-400 uppercase tracking-wide font-medium shrink-0">
              CAMPAIGN
            </span>
            <span className="text-[11px] text-right min-w-0">
              <span className="font-semibold text-slate-700 font-mono">{displayCampaignCode}</span>
              <span className="text-slate-500 ml-1">{displayCampaignLabel}</span>
            </span>
          </div>
        </div>
        <div className="text-left shrink-0 min-[420px]:ml-4 min-[420px]:text-right">
          <div className="flex items-center gap-1.5 min-[420px]:justify-end">
            <p className="text-[22px] font-bold text-slate-900 leading-none">{fmtEur(feeValue)}</p>
          </div>
          {isOverrideReadOnly ? (
            <div className="flex items-center mt-1 min-[420px]:justify-end">
              <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                {t('advisorPortal.serviceFeeOverrideBadge')}
              </span>
            </div>
          ) : null}
          <p className="text-[11px] text-slate-500 mt-0.5">fees</p>
        </div>
      </div>
    </div>
  );
}
